/**
 * Phase 4 + 5 client hooks (moat plan §6.2 starter kits, §7 flywheel & team):
 * public benchmark, template gallery, publish/instantiate template, task comments,
 * assignee, notification channels, benchmark opt-out.
 * Field names follow the routes under `src/routes/api/{public,plan,account,user,workspaces}`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qk } from '#/lib/api'

// ---------- benchmark
export interface BenchRow {
  agent: string
  tier: string
  kind: string
  layer: string
  tasks: number
  firstPassRate: number
  retries: number
  medianMinutes: number | null
}
export interface Benchmark {
  generatedAt: string
  sampleTasks: number
  byAgent: BenchRow[]
  byKind: BenchRow[]
  byTier: BenchRow[]
  monthly: { month: string; tasks: number; firstPassRate: number }[]
}

export const useBenchmark = () =>
  useQuery({
    queryKey: ['benchmark'],
    queryFn: () => api<Benchmark>('/api/public/benchmark'),
    staleTime: 300_000,
  })

/** Weighted first-pass rate over every sampled month (the headline number). */
export const overallFirstPass = (b: Benchmark | undefined) => {
  const n = b?.monthly.reduce((a, r) => a + r.tasks, 0) ?? 0
  if (!b || !n) return null
  return b.monthly.reduce((a, r) => a + r.firstPassRate * r.tasks, 0) / n
}

// ---------- templates
export interface TemplateCard {
  id: string
  slug: string
  title: string
  summary: string
  locale: string
  tags: string[]
  passRate: number | null
  verifiedTasks: number
  uses: number
  features: number
  createdAt: string
}
export interface TemplateDetail extends Omit<TemplateCard, 'features'> {
  features: {
    name: string
    summary: string | null
    phase: number
    icon: string | null
    subs: string[]
    tasks: number
  }[]
  tasks: number
}

export const usePublicTemplates = (tag?: string) =>
  useQuery({
    queryKey: ['public-templates', tag ?? ''],
    queryFn: () =>
      api<{ templates: TemplateCard[] }>(
        `/api/public/templates${tag ? `?tag=${encodeURIComponent(tag)}` : ''}`,
      ),
    staleTime: 60_000,
  })

export const usePublicTemplate = (slug: string) =>
  useQuery({
    queryKey: ['public-template', slug],
    queryFn: () =>
      api<{ template: TemplateDetail | null }>(
        `/api/public/templates?slug=${encodeURIComponent(slug)}`,
      ),
    retry: false,
  })

/** POST /api/plan/new/template {slug} → a fresh plan built from the snapshot (no PRD quota). */
export const useTemplateInstance = () =>
  useMutation({
    mutationFn: (input: { slug: string; workspaceId?: string }) =>
      api<{
        planId: string
        workspaceId: string
        features: number
        tasks: number
      }>('/api/plan/new/template', { method: 'POST', json: input }),
  })

export interface PublishedTemplate {
  id: string
  slug: string
  title: string
  passRate: string | null
  verifiedTasks: number
  isPublic: boolean
}

/** POST /api/plan/:id/template — 402 `pro_only`, 409 `not_enough_evidence`. */
export const usePublishTemplate = (planId: string) =>
  useMutation({
    mutationFn: (input: {
      title?: string
      summary?: string
      tags?: string[]
      isPublic?: boolean
    }) =>
      api<{ template: PublishedTemplate }>(`/api/plan/${planId}/template`, {
        method: 'POST',
        json: input,
      }),
  })

// ---------- task comments + assignee
export interface TaskComment {
  id: string
  body: string
  createdAt: string
  userId: string
  name: string
  image: string | null
}

export const useTaskComments = (taskId: string | null, enabled = true) =>
  useQuery({
    queryKey: ['task-comments', taskId ?? ''],
    queryFn: () =>
      api<{ comments: TaskComment[] }>(`/api/plan/tasks/${taskId}/comments`),
    enabled: Boolean(taskId) && enabled,
  })

export const useAddComment = (taskId: string | null) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api<{ comment: TaskComment }>(`/api/plan/tasks/${taskId}/comments`, {
        method: 'POST',
        json: { body },
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['task-comments', taskId ?? ''] }),
  })
}

export interface WorkspaceMember {
  id: string
  email: string
  role: 'member' | 'client' | string
  userId: string | null
  acceptedAt: string | null
  createdAt: string
  name: string | null
  image: string | null
}

export const useWorkspaceMembers = (
  workspaceId: string | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: ['ws-members', workspaceId ?? ''],
    queryFn: () =>
      api<{ members: WorkspaceMember[] }>(
        `/api/workspaces/${workspaceId}/members`,
      ),
    enabled: Boolean(workspaceId) && enabled,
    // a `member` seat the owner never invited is not an error — an empty list just means "only me"
    retry: false,
  })

/** PATCH /api/plan/tasks/:id {assigneeId} — `null` clears it. */
export const useSetAssignee = (planId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      assigneeId,
    }: {
      id: string
      assigneeId: string | null
    }) =>
      api<{ task: unknown }>(`/api/plan/tasks/${id}`, {
        method: 'PATCH',
        json: { assigneeId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tasks(planId) }),
  })
}

// ---------- notifications
export type NotifyEvent = 'checkpoint' | 'done' | 'stuck' | 'comment' | 'room' | 'inbox'
export const NOTIFY_EVENTS: NotifyEvent[] = [
  'checkpoint',
  'done',
  'stuck',
  'comment',
  'room',
  'inbox',
]
export type NotifyChannelKind = 'email' | 'telegram' | 'discord'
export interface NotificationChannel {
  id: string
  kind: NotifyChannelKind | string
  target: string
  events: string[]
  verifiedAt: string | null
  /** Inspirasi F5 — hour (0-23, Asia/Jakarta) of the daily digest; null = off */
  digestHour?: number | null
  createdAt: string
}

export const useNotificationChannels = (enabled = true) =>
  useQuery({
    queryKey: ['notify-channels'],
    queryFn: () =>
      api<{
        channels: NotificationChannel[]
        providers: { email: boolean; telegram: boolean; discord: boolean }
        discordInviteUrl: string | null
      }>('/api/account/notifications'),
    enabled,
  })

export const useAddNotificationChannel = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      kind: NotifyChannelKind
      target: string
      events: NotifyEvent[]
    }) =>
      api<{ channel: NotificationChannel }>('/api/account/notifications', {
        method: 'POST',
        json: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notify-channels'] }),
  })
}

/** Inspirasi F5 — set/clear the daily digest hour of one channel. */
export const useSetDigestHour = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; digestHour: number | null }) =>
      api<{ channel: NotificationChannel }>('/api/account/notifications', {
        method: 'PATCH',
        json: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notify-channels'] }),
  })
}

export const useRemoveNotificationChannel = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(
        `/api/account/notifications?id=${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notify-channels'] }),
  })
}

// ---------- profile (benchmark opt-out lives here)
export interface ProfileDto {
  id: string
  name: string
  email: string
  image: string | null
  locale: string
  role: string
  benchmarkOptOut: boolean
  /** non-tech F0: 'dev' | 'simple' */
  builderMode?: string
}

export const useProfile = (enabled = true) =>
  useQuery({
    queryKey: ['profile'],
    queryFn: () => api<{ user: ProfileDto }>('/api/user/profile'),
    enabled,
  })

export const useUpdateProfile = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: {
      name?: string
      image?: string | null
      locale?: 'en' | 'id'
      benchmarkOptOut?: boolean
    }) =>
      api<{ user: ProfileDto }>('/api/user/profile', {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  })
}
